import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, ChevronRight, X } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OnboardingBanner = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/status`);
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
    }
  };

  // Don't show if completed or dismissed or still loading
  if (!status || status.onboarding_completed || dismissed) {
    return null;
  }

  const steps = [
    { id: 'gmail_connected', label: 'Gmail' },
    { id: 'sms_verified', label: 'SMS' },
    { id: 'offer_template_setup', label: 'Offer Letter' },
    { id: 'first_job_created', label: 'Job Listing' },
    { id: 'careers_page_customized', label: 'Careers Page' }
  ];

  const completedCount = steps.filter(s => status[s.id]).length;
  const progressPercent = (completedCount / steps.length) * 100;

  return (
    <div 
      className="bg-gradient-to-r from-[#4B2D6E] to-[#5d3d82] text-white rounded-lg p-4 mb-6 shadow-lg"
      data-testid="onboarding-banner"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="hidden sm:flex items-center gap-1">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className={`w-3 h-3 rounded-full ${
                  status[step.id] ? 'bg-green-400' : 'bg-white/30'
                }`}
                title={step.label}
              />
            ))}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="h-5 w-5 text-[#E8722A]" />
              <span className="font-semibold">Complete Your Setup</span>
              <span className="text-sm text-white/70">
                {completedCount} of {steps.length} steps done
              </span>
            </div>
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden max-w-xs">
              <div 
                className="h-full bg-[#E8722A] transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate('/onboarding')}
            className="bg-[#E8722A] hover:bg-[#D6621F] text-white"
            size="sm"
            data-testid="continue-setup-btn"
          >
            Continue Setup
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDismissed(true)}
            className="text-white/70 hover:text-white hover:bg-white/10"
            data-testid="dismiss-banner-btn"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingBanner;
